import { Modal } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { InfoCircleFill, PlusCircle } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";
import { Input } from "./Controls/Input";

const MIN_SPOTS = 1;
const MAX_SPOTS = 300;

interface Props {
    show: boolean;
    onHide: () => void;
    onConfirm: (count: number) => void;
    /** Max spots that can be added this time (e.g. remaining room under plan cap). */
    maxCount?: number;
}

function newExtraSpotId() {
    return `extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createExtraSpotIds(count: number): string[] {
    return Array.from({ length: count }, () => newExtraSpotId());
}

/** Adds 1+ local placeholder rows (no API / no charge). Assign fills them later. */
export function GroupParkerAddSpotsModal({ show, onHide, onConfirm, maxCount = MAX_SPOTS }: Props) {
    const effectiveMax = Math.max(0, Math.min(MAX_SPOTS, maxCount));
    const [count, setCount] = useState("1");
    const [err, setErr] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const countInputRef = useRef<HTMLInputElement>(null);

    const focusCountInput = () => {
        const input = countInputRef.current;
        if (!input) return;
        input.focus();
        input.select();
    };

    useEffect(() => {
        if (show) {
            setCount(effectiveMax >= MIN_SPOTS ? "1" : "0");
            setErr(undefined);
            setSubmitting(false);
        }
    }, [show, effectiveMax]);

    const handleClose = () => {
        setSubmitting(false);
        onHide();
    };

    const handleConfirm = () => {
        const parsed = parseInt(count, 10);
        if (effectiveMax < MIN_SPOTS) {
            setErr(`Plan is limited to ${MAX_SPOTS} spots.`);
            return;
        }
        if (!Number.isInteger(parsed) || parsed < MIN_SPOTS || parsed > effectiveMax) {
            setErr(`Enter a number between ${MIN_SPOTS} and ${effectiveMax}.`);
            return;
        }
        setSubmitting(true);
        onConfirm(parsed);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered onEntered={focusCountInput}>
            <Modal.Header closeButton className="border-0 pb-0">
                <HeaderText>Add Open Spots</HeaderText>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3 small" role="alert">
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>
                    We'll create this many spots to fill in with parker details
                    </span>
                </div>
                <Input
                    ref={countInputRef}
                    type="number"
                    icon={<PlusCircle />}
                    label="How many parkers will you be adding?"
                    min={MIN_SPOTS}
                    max={Math.max(MIN_SPOTS, effectiveMax)}
                    value={count}
                    onChange={e => {
                        setCount(e.target.value);
                        setErr(undefined);
                    }}
                    error={err ? { type: "validate", message: err } : undefined}
                />
                <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button className="px-4" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        className="btn-main px-4"
                        onClick={handleConfirm}
                        disabled={submitting || maxCount < MIN_SPOTS}
                        loading={submitting}
                    >
                        Add and Assign Parker
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
